import { useSelector } from 'react-redux';
import { isSignupSendingBlocked } from '@/components/signup/activation';
import axios from 'axios';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

const EMPTY_LIST = [];

export const formatScheduledDate = (date) =>
  moment(date).calendar(null, {
    sameDay: '[Today at] LT',
    nextDay: '[Tomorrow at] LT',
    nextWeek: 'dddd [at] LT',
    sameElse: 'll [at] LT',
  });

// Canonical format for the localized date embedded in a scheduled-message event
// line. We use moment's `LLL` token, which App.jsx (setUpMoment) localizes per
// user via their account timezone: Australian users get day-first
// ('DD MMMM YYYY, h:mm a'), everyone else month-first ('MMMM DD YYYY, h:mm a').
// Creation, edit/reschedule, and text reconstruction all share it so the frozen
// line reads naturally for the user and pairing-by-text stays consistent.
export const SCHEDULED_EVENT_DATE_FORMAT = 'LLL';

export const formatScheduledEventText = (scheduledMessage) =>
  `Scheduled message for ${moment(scheduledMessage.scheduledSendDate).format(
    SCHEDULED_EVENT_DATE_FORMAT,
  )}: ${scheduledMessage.text}`;

// Every scheduled-message event line (upcoming, canceled, or sending now) shares
// the calendar icon so they read as one category in the thread.
const SCHEDULED_EVENT_PREFIXES = [
  'Scheduled message for',
  'Canceled scheduled message',
  'Sending scheduled message now',
];

export const isScheduledMessageEvent = (text) =>
  typeof text === 'string' &&
  SCHEDULED_EVENT_PREFIXES.some((prefix) => text.startsWith(prefix));

const SCHEDULED_FOR_PREFIX = 'Scheduled message for ';
// Older lines were written month-first or day-first with a comma before the
// time; newer ones use the account locale's LLL.
const SCHEDULED_EVENT_PARSE_FORMATS = [
  'MMMM D YYYY, h:mm a',
  'D MMMM YYYY, h:mm a',
  'MMMM D, YYYY h:mm a',
  'D MMMM YYYY h:mm a',
];

// Send date parsed from an upcoming event line, or null for other lines.
export const getScheduledEventDate = (text) => {
  if (typeof text !== 'string' || !text.startsWith(SCHEDULED_FOR_PREFIX)) return null;
  const separator = text.indexOf(': ');
  if (separator === -1) return null;
  const date = moment(
    text.slice(SCHEDULED_FOR_PREFIX.length, separator),
    SCHEDULED_EVENT_PARSE_FORMATS,
    'en',
    true,
  );
  return date.isValid() ? date.locale(moment.locale()) : null;
};

// Splits a frozen event line into a short label and the message preview, e.g.
// "Scheduled message for September 22 2026, 2:00 pm: Hi" ->
// { label: 'Scheduled for Sep 22, 2026 2:00 PM', preview: 'Hi' }.
export const splitScheduledEventText = (text) => {
  const separator = text.indexOf(': ');
  if (separator === -1) return { label: text, preview: '' };
  const label = text.slice(0, separator);
  const preview = text.slice(separator + 2);
  const date = getScheduledEventDate(text);
  return { label: date ? `Scheduled for ${date.format('lll')}` : label, preview };
};

/**
 * Shared state + actions for upcoming 1:1 scheduled messages.
 *
 * Used by both the conversation side panel (ScheduledMessageInfo) and the
 * inline thread view (ThreadScheduledMessages) so edit/reschedule/send-now/
 * delete behave identically wherever the user acts.
 */
const useScheduledMessages = ({
  customerId,
  refreshKey,
  enabled = true,
  onChanged,
  onResolved,
}) => {
  const sendingBlocked = useSelector(state => isSignupSendingBlocked(state.auth));
  const sendingBlockedRef = useRef(sendingBlocked);
  sendingBlockedRef.current = sendingBlocked;
  const [loading, setLoading] = useState(false);
  // The list is tagged with the customer it was fetched for so switching
  // conversations never shows the previous customer's messages while the new
  // request is in flight.
  const [fetched, setFetched] = useState({ customerId: null, scheduledMessages: [], requestedAt: null });
  const latestCustomerIdRef = useRef(customerId);
  latestCustomerIdRef.current = customerId;
  const isFetchedForCustomer = Boolean(customerId) && fetched.customerId === customerId;
  const scheduledMessages = isFetchedForCustomer ? fetched.scheduledMessages : EMPTY_LIST;
  // When the current list was requested (ms), or null before it first loads.
  // Scheduled messages created after this aren't in the list yet.
  const listedAt = isFetchedForCustomer ? fetched.requestedAt : null;
  const [error, setError] = useState(false);
  const [activeActionId, setActiveActionId] = useState(null);

  const [messageBeingEdited, setMessageBeingEdited] = useState(null);
  const [editedText, setEditedText] = useState('');
  const [messageBeingRescheduled, setMessageBeingRescheduled] = useState(null);
  const [editedScheduledDate, setEditedScheduledDate] = useState('');

  const getScheduledMessages = useCallback(async () => {
    if (!customerId) return;

    const requestedAt = Date.now();
    const setForCustomer = (list) => {
      // drop responses that land after the user has moved to another conversation
      if (latestCustomerIdRef.current !== customerId) return;
      setFetched({ customerId, scheduledMessages: list, requestedAt });
    };

    try {
      setLoading(true);
      setError(false);

      const res = await axios.post('/api/get-upcoming-scheduled-messages-for-customer', {
        _customer: customerId,
      });

      if (res.data.error) {
        setError(true);
        setForCustomer([]);
        return;
      }

      setForCustomer(res.data.scheduledMessages || []);
    } catch (err) {
      console.log(err);
      setError(true);
      setForCustomer([]);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: refreshKey is an intentional trigger to refetch when a scheduled message is created/changed elsewhere.
  useEffect(() => {
    if (!enabled) return;

    getScheduledMessages();
  }, [enabled, getScheduledMessages, refreshKey]);

  const handleAction = useCallback(
    async ({
      scheduledMessage,
      url,
      payload = {},
      successMessage,
      errorMessage,
      onSuccess,
      resolvedAction,
    }) => {
      if (resolvedAction !== 'delete' && sendingBlockedRef.current) {
        toast.error('Customer sending is unavailable for this account. Your message has not been queued.');
        return;
      }
      setActiveActionId(scheduledMessage._id);

      try {
        const res = await axios.post(url, {
          _id: scheduledMessage._id,
          _customer: customerId,
          ...payload,
        });

        if (res.data?.error) {
          toast.error(res.data.message || errorMessage);
          return;
        }

        toast.success(successMessage);
        onSuccess?.();
        await getScheduledMessages();
        onChanged?.();
        // Cancel/send-now leave a resolved marker in the thread; let the caller
        // mirror the server's rewritten event line without a refresh.
        if (resolvedAction) onResolved?.(scheduledMessage, resolvedAction);
      } catch (err) {
        console.log(err);
        toast.error(errorMessage);
      } finally {
        setActiveActionId(null);
      }
    },
    [customerId, getScheduledMessages, onChanged, onResolved],
  );

  const openEditDialog = useCallback((scheduledMessage) => {
    setMessageBeingEdited(scheduledMessage);
    setEditedText(scheduledMessage.text || '');
  }, []);

  const closeEditDialog = useCallback(() => {
    setMessageBeingEdited(null);
    setEditedText('');
  }, []);

  const openRescheduleDialog = useCallback((scheduledMessage) => {
    setMessageBeingRescheduled(scheduledMessage);
    setEditedScheduledDate(moment(scheduledMessage.scheduledSendDate).format('YYYY-MM-DDTHH:mm'));
  }, []);

  const closeRescheduleDialog = useCallback(() => {
    setMessageBeingRescheduled(null);
    setEditedScheduledDate('');
  }, []);

  const saveEditedMessage = useCallback(() => {
    if (!messageBeingEdited) return;

    handleAction({
      scheduledMessage: messageBeingEdited,
      url: '/api/update-scheduled-message-for-customer',
      payload: { text: editedText },
      successMessage: 'Scheduled message updated.',
      errorMessage: 'There was an error updating your scheduled message.',
      onSuccess: closeEditDialog,
    });
  }, [messageBeingEdited, editedText, handleAction, closeEditDialog]);

  const saveEditedDate = useCallback(() => {
    if (!messageBeingRescheduled) return;

    handleAction({
      scheduledMessage: messageBeingRescheduled,
      url: '/api/update-scheduled-message-for-customer',
      payload: {
        scheduledSendDate: moment(editedScheduledDate).utc().toDate(),
        // Localized string the server writes onto the thread event line, in the
        // canonical scheduled-message date format (shared with creation).
        localScheduledSendDate: moment(editedScheduledDate).format(
          SCHEDULED_EVENT_DATE_FORMAT,
        ),
      },
      successMessage: 'Scheduled time updated.',
      errorMessage: 'There was an error updating your scheduled time.',
      onSuccess: closeRescheduleDialog,
    });
  }, [messageBeingRescheduled, editedScheduledDate, handleAction, closeRescheduleDialog]);

  const sendNow = useCallback(
    (scheduledMessage) =>
      handleAction({
        scheduledMessage,
        url: '/api/send-scheduled-message-now-for-customer',
        successMessage: 'Message queued to send now.',
        errorMessage: 'There was an error sending your scheduled message now.',
        resolvedAction: 'send',
      }),
    [handleAction],
  );

  const deleteScheduledMessage = useCallback(
    (scheduledMessage) =>
      handleAction({
        scheduledMessage,
        url: '/api/delete-scheduled-message-for-customer',
        successMessage: 'Scheduled message deleted.',
        errorMessage: 'There was an error deleting your scheduled message.',
        resolvedAction: 'delete',
      }),
    [handleAction],
  );

  return {
    loading,
    error,
    scheduledMessages,
    listedAt,
    activeActionId,
    refetch: getScheduledMessages,
    sendNow,
    deleteScheduledMessage,
    dialogs: {
      sendingBlocked,
      messageBeingEdited,
      editedText,
      setEditedText,
      openEditDialog,
      closeEditDialog,
      saveEditedMessage,
      messageBeingRescheduled,
      editedScheduledDate,
      setEditedScheduledDate,
      openRescheduleDialog,
      closeRescheduleDialog,
      saveEditedDate,
      activeActionId,
    },
  };
};

export default useScheduledMessages;
